
import { baseUrl } from 'api/config'
import axios from 'axios'
// for organization updates
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbarContext } from '../../../context/SnackbarProvider/SnackbarProvider'
import { useUpdateComment } from '../comments/useUpdateComment' // New update hook for comments table

const extractS3Key = (url: string) => {
  try {
    const parts = new URL(url)
    return decodeURIComponent(parts.pathname.slice(1))
  } catch {
    console.warn('❌ Could not extract key from URL:', url)
    return null
  }
}

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { mutate: updateComment } = useUpdateComment() // New mutation for updating a single comment

  const handleDeleteAttachment = async (notes: any[], commentId: string, attachmentIndex: number) => {
    const note = notes.find(n => n.comment_uuid === commentId)
    if (!note || !note.attachments || !note.attachments[attachmentIndex]) return

    const file = note.attachments[attachmentIndex]
    const s3Key = extractS3Key(file.url)
    const thumbnailKey = file.thumbnailLink ? extractS3Key(file.thumbnailLink) : null

    const keysToDelete = [s3Key, thumbnailKey].filter(Boolean)

    if (keysToDelete.length === 0) return

    try {
      // 1. Delete both main file and thumbnail
      await axios.post(`${baseUrl}/aws/s3/attachments/delete-multiple`, {
        keys: keysToDelete,
      })
      console.log('🗑️ Deleted from S3:', keysToDelete)

      // 2. Update local state
      const updatedAttachments = note.attachments.filter((_, i) => i !== attachmentIndex)
      const updatedNote = { ...note, attachments: updatedAttachments }
      const _updatedNotes = notes.map(n => (n.comment_uuid === commentId ? updatedNote : n))

      // 3. Update in DynamoDB
      updateComment(
        {
          PK: note.PK,
          SK: note.SK,
          updates: [
            {
              fieldName: 'attachments',
              newValue: updatedAttachments,
            },
          ],
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['commentsByPk', note.PK] })
            queryClient.invalidateQueries({ queryKey: ['comments', note.PK] })
            queryClient.invalidateQueries({ queryKey: ['commentsByOrgId', note.orgId] })
            showSnackbar('Attachment deleted successfully', 'success')
          },
          onError: err => {
            console.error('❌ Failed to update comment after deleting attachment:', err)
            showSnackbar('Failed to update comment', 'error')
          },
        }
      )
    } catch (err) {
      console.error('❌ Failed to delete attachment from S3:', err)
      showSnackbar('Failed to delete attachment from S3', 'error')
    }
  }

  return { handleDeleteAttachment }
}
